import React, { useEffect, useMemo } from 'react';
import classnames from 'classnames';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import { deleteDraft, publishDraft, createDraft, setDraftInProgress, adminGetDraft } from '$/store/draft/actions';
import { setCurrentCompany } from '$/store/company/actions';
import { Routes, Route, useMatch } from 'react-router-dom';
import HeadBlock, { IPublishButton, ICompanyNewsLink } from '$/components/global/heading';
import HeadTab from '$/components/global/headingTab';
import CompanyOverview from './CompanyOverview';
import CompanyLoanAndRatings from './ratings-loans/CompanyLoanAndRatings';
import CompanyTransactionDetails from './CompanyTransactionDetails';
import CompanyAnalytics from './CompanyAnalytics';
import CompanyNews from './CompanyNews';
import CompanyTranscripts from './CompanyTranscriptions';
import CompanyDocuments from './CompanyDocuments';
import CompanySupplemental from './CompanySupplemental';
import CompanyAnalyst from './CompanyAnalyst';
import Loading from '$/components/loading/Loading';
import AdminCompanyDocumentHosting from './CompanyDocumentHosting';
import AdminCompanyDocumentHostingUpload from './CompanyDocumentHostingUpload';
import { getIsCurrentCompanyPublicState } from '$/store/company/selectors';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import { getDocumentHostingFeatureFlagState } from '$/store/release-feature-flags/selectors';
import { toastError } from '$/store/toast/actions';
import './index.scss';

export interface ICompanyProps {
  isCompanyLoading: boolean;
  isDraftLoading: boolean;
  error?: Bixby.Models.BixbyError;
  draftCompany?: Bixby.Models.DraftCompany;
  isEditing: boolean;
  currentCompany?: Bixby.Models.AdminCompany;
  featureFlags?: string[];
  isCurrentCompanyPublic: boolean;
  isSuperAdmin: boolean;
  isDocumentHostingEnabled: boolean;
  dispatchCreateDraftCompany: (companyId: string) => Promise<void>;
  dispatchDeleteDraftCompany: (companyId: string) => Promise<void>;
  dispatchPublishDraftCompany: (companyId: string) => Promise<void>;
  dispatchDraftInProgress: (isEditing: boolean) => Promise<void>;
  dispatchGetAdminCompany: (companyId: string) => Promise<void>;
  dispatchGetDraftCompany: (companyId: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
}

const Company: React.FC<ICompanyProps> = props => {
  const {
    draftCompany,
    currentCompany,
    isCompanyLoading,
    isDraftLoading,
    isEditing,
    featureFlags,
    isCurrentCompanyPublic,
    isSuperAdmin,
    dispatchDeleteDraftCompany,
    dispatchPublishDraftCompany,
    dispatchGetAdminCompany,
    dispatchDraftInProgress,
    dispatchCreateDraftCompany,
    dispatchGetDraftCompany,
    isDocumentHostingEnabled,
    error,
  } = props;

  const match = useMatch('/admin/company/:companyId/*');

  useEffect(() => {
    if (match?.params.companyId && match.params.companyId !== currentCompany?.id) {
      dispatchGetAdminCompany(match?.params.companyId);
      dispatchGetDraftCompany(match?.params.companyId);
    }
  }, [match, dispatchGetAdminCompany, dispatchGetDraftCompany, currentCompany?.id]);

  const handlePublishClick = () => {
    if (draftCompany) {
      dispatchPublishDraftCompany(draftCompany.id);
      dispatchDraftInProgress(false);
    }
  };

  const handleCancelClick = () => {
    if (draftCompany) {
      dispatchDeleteDraftCompany(draftCompany.id);
      dispatchDraftInProgress(false);
    }
  };

  const handleEditClick = () => {
    if (match?.params.companyId && !draftCompany) {
      dispatchCreateDraftCompany(match?.params.companyId);
    } else {
      setDraftInProgress(true);
    }
  };

  const userCanPublish = useMemo(() => {
    if (!currentCompany || Bixby.Utils.isNullOrEmpty(currentCompany)) {
      return false;
    }

    const isPublic = currentCompany.isSECPublic;
    const hasLastPublishedDate = !!currentCompany.lastPublishedDate;
    // if the company is public and the company hasn't been published, only super admins can do the initial publish
    if (isPublic && !hasLastPublishedDate) {
      return isSuperAdmin;
    } else {
      return true;
    }
  }, [currentCompany, isSuperAdmin]);

  const publishProps: IPublishButton = {
    enabled: false,
    publishEnabled: true,
    seeAllLink: '/admin/company',
    onPublishClick: handlePublishClick,
    onCancelClick: handleCancelClick,
    onEditClick: handleEditClick,
    editEnabled: true,
    // any admin user can publish if 1) the current company is not an SEC public company, or if the current (SEC public) company has a valid last published date
    userCanPublish,
    userCannotPublishMessage:
      'This public company has not yet been published. A Super Admin must be the first user to publish this company for the first time.',
  };

  const companyNewsLinkProps: ICompanyNewsLink = {
    sendCompanyUpdateLink: currentCompany ? `admin/send-company-update/${currentCompany.id}` : null,
  };

  const baseUrl = `/admin/company/${match?.params.companyId}`;
  const loanDetailsUrl = `${baseUrl}/loan-details`;
  const transactionDetailsUrl = `${baseUrl}/transaction-details`;
  const anaylitcsUrl = `${baseUrl}/analytics`;
  const newsUrl = `${baseUrl}/news`;
  const documentsUrl = `${baseUrl}/documents`;
  const supplementalUrl = `${baseUrl}/supplemental`;
  const analystUrl = `${baseUrl}/analyst`;
  const transcriptsUrl = `${baseUrl}/transcripts`;
  const documentHostingUrl = `${baseUrl}/document-hosting`;

  let conditionalLinkProps = {};
  if (!Bixby.Configuration.isStandAloneMode) {
    conditionalLinkProps = {
      target: '_blank',
    };
  }

  const getCompanyNameDisplay = company => (
    <>
      {company.name} | <span className={classnames({ flagged: company.flagged })}>{company.bixbyId}</span>
    </>
  );

  const company = isEditing ? draftCompany : currentCompany;
  const uniqueNameElement = !company ? undefined : (
    <div>
      {currentCompany?.creditSightsCompanyId ? (
        <div className="link">
          <a
            href={`${Bixby.Configuration.creditSightsUrl}/companies/${currentCompany?.creditSightsCompanyId}`}
            {...conditionalLinkProps}>
            {getCompanyNameDisplay(company)}
          </a>
        </div>
      ) : (
        getCompanyNameDisplay(company)
      )}
    </div>
  );

  if (isCompanyLoading || isDraftLoading) {
    return <Loading pageLevel />;
  } else if (error) {
    return <p className="error">An error occurred trying to get the company: {error.message}</p>;
  } else {
    return currentCompany && !Bixby.Utils.isNullOrEmpty(currentCompany) ? (
      <React.Fragment>
        <HeadBlock
          titleElement={uniqueNameElement}
          publishButton={publishProps}
          companyNewsLink={companyNewsLinkProps}
          isCompanySECPublic={isCurrentCompanyPublic}
          declarationType={currentCompany.declarationType}
          adminCompanyExportEnabled
          title={''}>
          <HeadTab title="Profile &amp; Access" link={baseUrl} />
          <HeadTab title="Ratings &amp; Loan Details" link={loanDetailsUrl} exact={true} />
          <HeadTab title="Transaction Details" link={transactionDetailsUrl} exact={true} />
          <HeadTab title="Analytics" link={anaylitcsUrl} exact={true} />
          <HeadTab title="News" link={newsUrl} exact={true} />
          <HeadTab title="Transcripts" link={transcriptsUrl} exact={true} />
          <HeadTab title="Documents" link={documentsUrl} exact={true} />
          {isDocumentHostingEnabled && (featureFlags || []).includes(Bixby.Constants.CLIENT_COMPANY_DOCUMENTS) && (
            <HeadTab title="Document Hosting" link={documentHostingUrl} exact={true} />
          )}
          <HeadTab title="Supplemental" link={supplementalUrl} exact={true} />
          <HeadTab title="Analyst" link={analystUrl} exact={true} />
        </HeadBlock>
        <div>
          <Routes>
            <Route path={'/*'} element={<CompanyOverview />} />
            <Route path={'/loan-details'} element={<CompanyLoanAndRatings />} />
            <Route path={'/transaction-details'} element={<CompanyTransactionDetails />} />
            <Route path={'/analytics'} element={<CompanyAnalytics />} />
            <Route path={'/news'} element={<CompanyNews />} />
            <Route path={'/transcripts'} element={<CompanyTranscripts />} />
            <Route path={'/documents'} element={<CompanyDocuments />} />
            <Route path={'/supplemental'} element={<CompanySupplemental />} />
            <Route path={'/analyst'} element={<CompanyAnalyst />} />
            {isDocumentHostingEnabled && (featureFlags || []).includes(Bixby.Constants.CLIENT_COMPANY_DOCUMENTS) && (
              <>
                <Route path={'/document-hosting'} element={<AdminCompanyDocumentHosting />} />
                <Route path={'/document-hosting/upload'} element={<AdminCompanyDocumentHostingUpload />} />
                <Route path={'/document-hosting/upload/:emailId'} element={<AdminCompanyDocumentHostingUpload />} />
              </>
            )}
          </Routes>
        </div>
      </React.Fragment>
    ) : null;
  }
};

const mapStateToProps = (state: AppState) => {
  const { isCurrentCompanyPublic } = getIsCurrentCompanyPublicState(state);
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  const { isDocumentHostingEnabled } = getDocumentHostingFeatureFlagState(state);

  return {
    isCurrentCompanyPublic,
    isSuperAdmin,
    isDocumentHostingEnabled,
    isCompanyLoading: state.companyState.isLoading,
    error: state.companyState.error,
    isDraftLoading: state.draftState.isLoading,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    currentCompany: state.companyState.currentCompany,
    featureFlags: state.loginState.featureFlags,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchCreateDraftCompany: (companyId: string) => dispatch(createDraft(companyId)),
  dispatchDeleteDraftCompany: (companyId: string) => dispatch(deleteDraft(companyId)),
  dispatchPublishDraftCompany: (companyId: string) => dispatch(publishDraft(companyId)),
  dispatchDraftInProgress: (isEditing: boolean) => dispatch(setDraftInProgress(isEditing)),
  dispatchGetAdminCompany: (companyId: string) => dispatch(setCurrentCompany(companyId)),
  dispatchGetDraftCompany: (companyId: string) => dispatch(adminGetDraft(companyId)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Company);
